import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MousePointerClick } from 'lucide-react';

const P = process.env.PUBLIC_URL || '';
const BASE_PAGES = [
  `${P}/cards/page1.png`,
  `${P}/cards/img-earmuffs.jpg`,
  `${P}/cards/img-handhold.jpg`,
  `${P}/cards/page2-couple.jpg`,
];

export default function Flipbook() {
  const [flipped, setFlipped] = useState(0);
  const [touched, setTouched] = useState(false);
  const turningRef = useRef(false);
  const pages = BASE_PAGES;

  useEffect(() => {
    BASE_PAGES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  const turn = () => {
    if (turningRef.current) return;
    turningRef.current = true;
    setTimeout(() => {
      turningRef.current = false;
    }, 1250);
    setTouched(true);
    setFlipped((f) => (f < pages.length - 1 ? f + 1 : 0));
  };

  return (
    <section id="flipbook" className="relative flex flex-col items-center py-10 md:py-14">
      <div className="spotlight" />
      <motion.div
        className="stage"
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="frame-echo" />
        <AnimatePresence>
          {!touched && (
            <motion.div
              className="tap-badge"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1, transition: { delay: 1.4, duration: 0.6, ease: [0.16, 1, 0.3, 1] } }}
              exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.3 } }}
              data-testid="tap-badge"
            >
              <span className="tap-ring" />
              <MousePointerClick size={15} />
              <span>tap to open</span>
            </motion.div>
          )}
        </AnimatePresence>
        <div
          className={`book ${!touched ? 'hint' : ''}`}
          onClick={turn}
          role="button"
          aria-label="Turn the invitation page"
          data-testid="flipbook-stage"
        >
          {pages.map((src, i) => (
            <div
              key={i}
              className={`sheet ${i < flipped ? 'flipped' : ''} ${i === flipped ? 'top' : ''}`}
              style={{ zIndex: i < flipped ? i + 1 : 20 - i }}
            >
              <div className="face front">
                <img src={src} alt={`Invitation page ${i + 1}`} draggable="false" />
              </div>
              <div className="face back">
                <svg viewBox="0 0 120 120" className="medallion">
                  <g fill="none" stroke="#DDBE76" strokeWidth="1.4">
                    <circle cx="60" cy="60" r="34" />
                    <circle cx="60" cy="60" r="26" strokeDasharray="3 5" />
                    <circle cx="60" cy="18" r="5" />
                    <circle cx="90" cy="30" r="5" />
                    <circle cx="102" cy="60" r="5" />
                    <circle cx="90" cy="90" r="5" />
                    <circle cx="60" cy="102" r="5" />
                    <circle cx="30" cy="90" r="5" />
                    <circle cx="18" cy="60" r="5" />
                    <circle cx="30" cy="30" r="5" />
                  </g>
                  <text x="60" y="72" textAnchor="middle" fontSize="30" fill="#DDBE76" style={{ fontFamily: 'Galada, serif' }}>
                    শুভ
                  </text>
                </svg>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      <div className="mt-8 flex items-center gap-2.5" data-testid="flipbook-dots">
        {pages.map((_, i) => (
          <span key={i} className={`dot ${i === flipped ? 'active' : ''}`} />
        ))}
      </div>
      <p className="mt-3 font-english italic text-sm md:text-base text-kula-ink/60">
        tap the card to turn the page
      </p>
    </section>
  );
}
